import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { IAlarmLynxGridModel } from "./AlarmLynxSummaryModels";
import { ICompressorLynxGridModel } from "./CompressorLynxSummaryModels";
import { IGroupByAttribute, IPolledDeviceSectionsModel, IPolledDevicesGridRestQueryParams, IPolledDevicesGridRow, PolledDeviceSectionsModel } from "./DeviceSummaryModels";
import { WellSummaryModel, IWellSummaryGridRestQueryParams, IWellSummaryModel, IWellSummaryGridRow } from "./WellSummaryModels";
//import { ISummaryListingStateModel } from "./BaseModels";
import OfflineDataService from "../offline-data-service/OfflineDataService";
import Enumerable from "linq";
import { SystemBalanceTypeEnum } from "../../common/enums/SystemBalanceTypeEnum";
import * as DRCX from "../../common/enums/DRCXEnums";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { ITankBatterySummaryGridModel, ITankBatterySummaryGridRow, ITankBatterySummaryGridTagCell, ITankBatterySummaryGridTankRow } from "./TankBatterySummaryModels";
import { IExportOptions } from "./BaseModels";
import restService from "../rest-service/RestService";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";

type GroupByAttributeQueryParms = {
    name: string
}

const SummaryListingDataService = {

    getDeviceData: async function (customerID: number, groupID: number, offline: boolean, configurationID?: number, correlationID?: string, signal?: AbortSignal): Promise<IPolledDeviceSectionsModel> {

        // Bug 53890: Delta Utilities - Error on Summary Listing - DOC.  Seems that react query get cached with parameters
        // even when not enabled.  Code in group adminstration save queries attempts to refect all summary data cached queries 
        // when changes are made, which appears to have called the backend code with an invalid configurationID (-1)
        if (configurationID === -1) {

            return {

                isSystemBalanceGroup: false,
                gridData: [],
                deviceCount: 0,
                sectionCount: 0,
                groupByAttributes: [],
                configurationID: -1,
                userPermissions: [],
            } as IPolledDeviceSectionsModel;
        }

        if (!offline) {
            // path = groups/[groupID]/summarydata/polled-devices
            const path: string = [
                "v3",
                "groups",
                groupID,
                "summarydata",
                "polled-devices"
            ].join("/");

            const params = {
                configurationID: configurationID,
                correlationID: correlationID
            };

            const response = await restService.get<PolledDeviceSectionsModel, IPolledDevicesGridRestQueryParams>(path, signal, params);

            //console.log(`dev configID worked: ${groupID} ${configurationID}`);
            return new PolledDeviceSectionsModel(response.data);
        } else {
            const offlineDevices = OfflineDataService.getGroupDevices(customerID,
                groupID,
                false,
                ObjectFeaturesEnum.ManualEntry);

            const group = OfflineDataService.getGroup(customerID, groupID);

            const [offlineDeviceData, groupData] = await Promise.all([offlineDevices, group]);

            let order = 0;
            const model: IPolledDeviceSectionsModel = {
                deviceCount: offlineDeviceData.length,
                isSystemBalanceGroup: groupData?.isSystemBalance,
                gridData: [{
                    caption: "",
                    columns: [{
                        field: "displayText",
                        headerText: "Name",
                        sortable: true
                    }],

                    rows: Enumerable.from(offlineDeviceData)
                        .select(o => {
                            const item: IPolledDevicesGridRow = {
                                deviceID: o.id,
                                deviceName: o.name,
                                systemBalanceType: o.systemBalanceType ?? SystemBalanceTypeEnum.Inlet,
                                hasTagThreads: false,
                                hasCommError: false,
                                hasRecentMemo: false,
                                hasArchives: false,
                                hasDeviceTrending: false,
                                hasLiveData: false,
                                hasPlungerLiftRunStatistics: false,
                                hasMarketingReport: false,
                                hasTankGaugeSheet: false,
                                hasDynacardView: false,
                                hasIssuesAndActions: false, //@@needed??? also should these just be optional?
                                memoText: "",
                                memoTimestamp: "",
                                memoID: undefined,
                                memoCreatedBy: "",
                                alarmStatus: 0,
                                order: order++,
                                active: true,
                                cells: [{
                                    displayText: o.name,
                                    objectType: eLynxObjectTypeEnum.Device,
                                    objectID: o.id,
                                    inAlarm: false, //@@
                                    backgroundColor: "",
                                    inStaleDataAlarm: false,
                                    staleDataColor: "",
                                    optionalColumn: DRCX.OptionalColumnEnum.DeviceDisplayName,
                                    isAttributeCell: false
                                }],
                                latitude: undefined,
                                longitude: undefined,
                                hasMemoModify: false

                            };

                            return item;
                        })
                        .orderBy(o => o.deviceName.toLowerCase())
                        .toArray()
                }]
            };


            return model;
        }
    },

    getWellData: async function (customerID: number, groupID: number, configurationID: number, correlationID: string, offline: boolean, signal?: AbortSignal): Promise<IWellSummaryModel> {

        if (!offline) {
            const path: string = [
                "groups",
                groupID,
                "summarydata",
                "wells"
            ].join("/");

            const params = {
                configurationID: configurationID,
                correlationID: correlationID
            };


            const response = await restService.get<WellSummaryModel, IWellSummaryGridRestQueryParams>(path, signal, params);

            //(`well configID worked: ${groupID} ${configurationID}`);
            return new WellSummaryModel(response.data);
        } else {

            const wells = await OfflineDataService.getGroupWells(customerID,
                groupID,
                ObjectFeaturesEnum.ManualEntry);


            const model: IWellSummaryModel = {

                userPermissions: [],
                gridData: {
                    caption: "",
                    columns: [{
                        field: "displayText",
                        headerText: "Name",
                        sortable: true
                    }],


                    rows: Enumerable.from(wells)
                        .select(o => {
                            const item: IWellSummaryGridRow = {
                                wellID: o.id,
                                wellName: o.name,
                                systemBalanceType: SystemBalanceTypeEnum.Other,
                                hasTagThreads: false,
                                hasCommError: false,
                                hasRecentMemo: false,
                                hasArchives: false,
                                hasLiveData: false,
                                hasPlungerLiftRunStatistics: false,
                                hasMarketingReport: false,
                                hasTankGaugeSheet: false,
                                hasDynacardView: false,
                                hasIssuesAndActions: false,
                                hasWellTrending: false,
                                memoText: "",
                                memoTimestamp: "",
                                memoID: undefined,
                                alarmStatus: 0,
                                active: true,
                                cells: [{
                                    displayText: o.name,
                                    objectType: eLynxObjectTypeEnum.Well,
                                    objectID: o.id,
                                    inAlarm: false,
                                    backgroundColor: "",
                                    inStaleDataAlarm: false,
                                    staleDataColor: "",
                                    optionalColumn: DRCX.OptionalColumnEnum.WellName,
                                    isAttributeCell: false
                                }]
                            };

                            return item;
                        })
                        .orderBy(o => o.wellName.toLowerCase())
                        .toArray()
                }
            };

            return model;
        }
    },

    getTankBatteryData: async function (customerID: number, groupID: number, includeTanks: boolean, offline: boolean, signal?: AbortSignal): Promise<ITankBatterySummaryGridModel> {

        if (!offline) {
            // path = groups/[groupID]/summarydata/tankbatteries
            const path: string = [
                "groups",
                groupID,
                "summarydata",
                "tankbatteries"
            ].join("/");

            const params = {
                includeTanks: includeTanks
            };

            const response = await restService.get<ITankBatterySummaryGridModel, { includeTanks: boolean; }>(path, signal, params);
            return response.data;
        } else {
            const tankBatteries = await OfflineDataService.getGroupTankBatteries(customerID,
                groupID,
                ObjectFeaturesEnum.ManualEntry);

            const model: ITankBatterySummaryGridModel = {

                caption: "",
                columns: [{
                    field: "displayText",
                    headerText: "Name",
                    sortable: true
                }],
                rows: Enumerable.from(tankBatteries)
                    .select(o => {
                        const item: ITankBatterySummaryGridRow = {
                            id: o.id,
                            displayText: o.name,
                            tags: new Array<ITankBatterySummaryGridTagCell>(),
                            tanks: new Array<ITankBatterySummaryGridTankRow>()
                        };

                        return item;
                    })
                    .orderBy(o => o.displayText.toLowerCase())
                    .toArray()
            };

            return model;
        }
    },

    getCompressorLynxData: async function (groupID: number, signal?: AbortSignal): Promise<ICompressorLynxGridModel> {

        // path = groups/[groupID]/summarydata/compressorlynx
        const path: string = [
            "groups",
            groupID,
            "summarydata",
            "compressorlynx"
        ].join("/");

        const response = await restService.get<ICompressorLynxGridModel>(path, signal);
        return response.data;
    },

    getAlarmLynxData: async function (groupID: number, signal?: AbortSignal): Promise<IAlarmLynxGridModel> {

        // path = groups/[groupID]/summarydata/alarmlynx
        const path: string = [
            "groups",
            groupID,
            "summarydata",
            "alarmlynx"
        ].join("/");

        const response = await restService.get<IAlarmLynxGridModel>(path, signal);
        return response.data;
    },

    getGroupByAttributeData: async function (groupID: number, attributeName: string, signal?: AbortSignal): Promise<IGroupByAttribute[]> {

        const prams: GroupByAttributeQueryParms = {
            name: attributeName
        }
        const path = `groups/${groupID}/summarydata/polled-devices/attributes`;
        const response = await restService.get<IGroupByAttribute[], GroupByAttributeQueryParms>(path, signal, prams);
        return response.data;
    },


    // getSparklinesData: async function (contextGroupID: number, correlationID: string, tatIDParameters: Array<ReportTrendModels.ITagTrendDataParameter>):
    //     Promise<Array<ReportTrendModels.ITagTrendData>> {
    // getSparklinesData: async function (contextGroupID: number, correlationID: string, tatIDParameters: Array<any>, signal?: AbortSignal):
    //     Promise<Array<any>> {

    //     const path: string = [
    //         "groups",
    //         contextGroupID,
    //         "summarydata",
    //         "sparklines"
    //     ].join("/");

    //     const params = {
    //         "correlationID": correlationID,
    //         "tagTrendDataParameters": tatIDParameters
    //     };

    //     const response = await restService.post<any, Array<any>>(path, params, signal);
    //     return response.data;
    // },

    exportData: async function (
        //notificationKey: string,
        groupID: number,
        //configurationID: number,
        //wellPageConfigID: number,
        options: IExportOptions,        
        mimeType?: ApplicationMimeTypesEnum,
        //sortBySystemBalanceType: boolean = false,
        //sortByDevicesInAlarmFirst: boolean = true,
        //sortByRecentMemo: boolean = false,
        //sortAscending: boolean = true,
        //sortColumnIndex: number = 0,
        //deviceIDs?: Array<number>,
        //sortByWellsInAlarmFirst: boolean = true,
        //sortWellsAscending: boolean = true,
        //sortWellsColumnIndex: number = 0,
        //wellIDs?: Array<number>,
        //searchTerm?: string

    ): Promise<void> {

        // path = groups/[groupID]/summarydata/export
        const path: string = [
            "groups",
            groupID,
            "summarydata",
            "export"
        ].join("/");

        // const params = {
        //     notificationKey: options.notificationKey,
        //     configurationID: options.configurationID,
        //     wellPageConfigID: options.wellPageConfigID,
        //     mimeType: mimeType,
        //     sortBySystemBalanceType: sortBySystemBalanceType,
        //     sortByDevicesInAlarmFirst: sortByDevicesInAlarmFirst,
        //     sortByRecentMemo: sortByRecentMemo,
        //     sortAscending: sortAscending,
        //     sortColumnIndex: sortColumnIndex,
        //     deviceIDs: deviceIDs,
        //     sortByWellsInAlarmFirst: sortByWellsInAlarmFirst,
        //     sortWellsAscending: sortWellsAscending,
        //     sortWellsColumnIndex: sortWellsColumnIndex,
        //     wellIDs: wellIDs,
        //     searchTerm: searchTerm
        // };

        await restService.post(path, options);
    }
};

export default SummaryListingDataService;